import { useMemo, useState, type ReactNode, type Ref } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { AssistantLinkRef } from '@contracts/assistant';
import { overflowNoun, overflowOf } from '@contracts/assistant-briefing';
import type {
  BriefingDTO, BriefingExternalDTO, BriefingGroupDTO, BriefingItemDTO, BriefingTone,
} from '@contracts/assistant-proactive';
import { TEXT } from '@/components/typography';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { avatarTone, serverInitial } from '../../assistant-admin/server-tone';
import type { Emotion } from '../emotion';
import { Hana } from '../Hana';
import { useOwnAppearance } from '../api';
import { parseObjLinks, Markdown } from '../markdown';
import { ObjectChip, refKey, useResolved, type ChipState } from '../ObjectChip';

/**
 * The body of a briefing (S3 §8.4, mockup §1 and §5; L3 D125): Hana with the briefing's face, the title and its when
 * line, Hana's words with their chips, and the non-empty groups. The Dashboard card wraps it with its live parts (the
 * typewriter, ⋯, "Đã xem"); the history (T11.2) shows it read-only. Every chip of the text and the rows is named by ONE
 * resolve, so a name the reader lost access to never shows.
 */

const PILL: Record<BriefingTone, NonNullable<BadgeProps['variant']>> = {
  late: 'danger', warn: 'warning', ok: 'success', info: 'info',
};

/** A group is drawn when it has anything to show: rows, or a count (the server already drops empty sections). */
const isShown = (g: BriefingGroupDTO): boolean => g.count > 0 || g.items.length > 0;

export type BriefingViewProps = {
  briefing: BriefingDTO;
  /** What the typewriter shows so far; the whole text when it is not typing. */
  text?: string;
  typing?: boolean;
  /** Hana's face; the briefing's own emotion by default ('think' while a recompose runs). */
  emotion?: Emotion;
  still?: boolean;
  /** A phone: one column, the small Hana in the header row (mockup §5). */
  phone?: boolean;
  /** One column whatever the screen: the 380 px panel's history (T11.2). The Dashboard keeps its grid. */
  compact?: boolean;
  /** The header's right side (the ⋯ menu). */
  headActions?: ReactNode;
  /** The actions row under the groups. */
  footer?: ReactNode;
  /** Makes the title a focus target (`tabIndex=-1`): where focus lands when this view replaces the control pressed. */
  titleRef?: Ref<HTMLHeadingElement>;
};

export function BriefingView({ briefing, text, typing = false, emotion, still = false, phone = false, compact = false,
  headActions, footer, titleRef }: BriefingViewProps) {
  const appearance = useOwnAppearance();
  const groups = briefing.groups.filter(isShown);
  const refs = useMemo(() => {
    const out: AssistantLinkRef[] = parseObjLinks(briefing.text).map(({ type, id }) => ({ type, id }));
    for (const g of briefing.groups) for (const i of g.items) if (i.ref !== null) out.push(i.ref);
    return out;
  }, [briefing]);
  const resolved = useResolved(refs);
  // D126: the first two non-empty groups open. Which boxes sit in the top row is fixed by that default, so opening or
  // closing one never moves another.
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set(groups.slice(0, 2).map((g) => g.section)));
  const toggle = (section: string) => setOpen((was) => {
    const next = new Set(was);
    if (next.has(section)) next.delete(section); else next.add(section);
    return next;
  });
  const primary = groups.slice(0, 2);
  const others = groups.slice(2);
  const face = <Hana appearance={appearance} emotion={emotion ?? briefing.emotion} size={phone ? 34 : 56} still={still} />;
  const box = (g: BriefingGroupDTO) => (
    <Group key={g.section} group={g} open={open.has(g.section)} onToggle={() => toggle(g.section)} resolved={resolved} />
  );

  return (
    <div className={cn('flex gap-3.5', phone && 'flex-col gap-2')}>
      {!phone && <div className="flex-none">{face}</div>}
      <div className="flex min-w-0 flex-1 flex-col">
        <div data-briefing-head className="mb-1.5 flex items-center gap-2">
          {phone && <div className="flex-none">{face}</div>}
          <div className={cn('flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2', phone && 'flex-col gap-0')}>
            <h3 ref={titleRef} tabIndex={titleRef === undefined ? undefined : -1}
              className={cn(TEXT.sectionTitle, titleRef !== undefined && 'outline-none')}>{briefing.title}</h3>
            <span className="text-xs text-[hsl(var(--h-muted-foreground))]">{briefing.when}</span>
          </div>
          {headActions}
        </div>
        <div data-briefing-text data-typing={typing ? '' : undefined}
          className="text-[0.84375rem] leading-[1.6] text-[hsl(var(--h-card-foreground))] [&_p+p]:mt-2">
          <Markdown text={text ?? briefing.text} renderObject={(o) => (
            <ObjectChip objectRef={{ type: o.type, id: o.id }} resolved={resolved.get(refKey(o))} />
          )} />
        </div>
        {primary.length > 0 && (
          <div data-briefing-groups className={cn('mt-3 grid items-start gap-2.5', !compact && 'sm:grid-cols-2 lg:grid-cols-3')}>
            {primary.map(box)}
          </div>
        )}
        {others.length > 0 && (
          <div data-briefing-groups className={cn('mt-2 grid items-start gap-2.5', !compact && 'sm:grid-cols-2 lg:grid-cols-4')}>
            {others.map(box)}
          </div>
        )}
        {footer}
      </div>
    </div>
  );
}

function Group({ group, open, onToggle, resolved }: {
  group: BriefingGroupDTO; open: boolean; onToggle: () => void; resolved: ReadonlyMap<string, ChipState>;
}) {
  const [all, setAll] = useState(false);
  // D125: the chip rows overflow ("+ n việc nữa", then "+ k việc khác" past the DTO's rows); the weekly totals (rows
  // without a chip) always show, after them.
  // S6 §10: the outside rows carry no chip but overflow as the chip rows do (a section of up to ten), counted in "mục".
  const listed = (i: BriefingItemDTO) => i.ref !== null || i.external !== undefined;
  const chips = group.items.filter(listed);
  const totals = group.items.filter((i) => !listed(i));
  const over = overflowOf(group.count, chips.length, overflowNoun(group.section));
  const rows = [...(all ? chips : chips.slice(0, over.initial)), ...totals];
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div data-briefing-group={group.section} className="min-w-0 rounded-lg border border-[hsl(var(--h-border))] px-2.5 py-2 pointer-coarse:py-0">
      {/* Under a thumb the heading button carries the 44 px floor (styles.css), so the box drops its own padding. */}
      <button type="button" aria-expanded={open} onClick={onToggle}
        className="-mx-1 flex w-[calc(100%+0.5rem)] items-center justify-between gap-2 rounded-md px-1 py-0.5 text-left text-xs font-bold text-[hsl(var(--h-card-foreground))] outline-none hover:bg-[hsl(var(--h-muted))] focus-visible:bg-[hsl(var(--h-muted))]">
        <span className="min-w-0">{group.heading}</span>
        <Chevron aria-hidden="true" className="size-3.5 flex-none text-[hsl(var(--h-muted-foreground))]" />
      </button>
      {open && (rows.length > 0 || over.more !== null || over.rest !== null) && (
        <div className="mt-1 pointer-coarse:mt-0 pointer-coarse:pb-1">
          {rows.map((item, i) => (item.external !== undefined
            ? <ExternalRow key={`x${i}`} item={item} external={item.external} />
            : <Row key={item.ref === null ? `t${i}` : refKey(item.ref)} item={item} resolved={resolved} />))}
          {over.more !== null && !all && (
            <button type="button" onClick={() => setAll(true)}
              className="block w-full rounded-none border-t border-dashed border-[hsl(var(--h-border))] py-1 text-left text-xs text-[hsl(var(--h-muted-foreground))] outline-none hover:text-[hsl(var(--h-primary))] hover:underline focus-visible:bg-[hsl(var(--h-muted))]">
              {over.more}
            </button>
          )}
          {over.rest !== null && (all || over.more === null) && (
            <p className="border-t border-dashed border-[hsl(var(--h-border))] py-1 text-xs text-[hsl(var(--h-muted-foreground))]">{over.rest}</p>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ item, resolved }: { item: BriefingItemDTO; resolved: ReadonlyMap<string, ChipState> }) {
  return (
    <div data-briefing-row
      className="flex items-center justify-between gap-2 border-t border-dashed border-[hsl(var(--h-border))] py-1 text-xs">
      <span className="min-w-0">
        {item.ref === null
          ? <span className="text-[hsl(var(--h-card-foreground))]">{item.label}</span>
          : <ObjectChip objectRef={item.ref} resolved={resolved.get(refKey(item.ref))} />}
      </span>
      <Badge variant={PILL[item.tone]} data-tone={item.tone} className="flex-none px-[7px] py-0 text-[11px] font-bold">
        {item.hint}
      </Badge>
    </div>
  );
}

/** Only an absolute `https:` address becomes a link (S6 §10; the server already keeps no other, checked again here). */
export function httpsUrl(url: string | undefined): string | null {
  if (url === undefined) return null;
  try {
    return new URL(url).protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

/**
 * An outside row (S6 §10): the server's letter square in its fixed colour (the Workplace outside chip's, mockup §4
 * `.chip.ext .srv`), the item's title — plain text, one line, ellipsis, the full "<server> · <title>" on hover — a link
 * to its `https:` address in a new tab that never gets this page (`noopener noreferrer`), the sender muted, the clock.
 */
function ExternalRow({ item, external }: { item: BriefingItemDTO; external: BriefingExternalDTO }) {
  const href = httpsUrl(external.url);
  const words = `${external.server} · ${item.label}`;
  const title = 'min-w-0 truncate text-[hsl(var(--h-card-foreground))]';
  return (
    <div data-briefing-row data-ext-kind={external.kind}
      className="flex items-center justify-between gap-2 border-t border-dashed border-[hsl(var(--h-border))] py-1 text-xs">
      <span className="flex min-w-0 items-center gap-1.5">
        <span data-srv="" aria-hidden="true" className={cn(
          'grid size-3.5 flex-none place-items-center rounded-[4px] text-[8px] font-extrabold leading-none text-white',
          avatarTone(external.serverId))}>
          {serverInitial(external.server)}
        </span>
        <span className="sr-only">{external.server}: </span>
        {href === null
          ? <span className={title} title={words}>{item.label}</span>
          : (
            <a href={href} target="_blank" rel="noopener noreferrer" title={words}
              className={cn(title, 'font-semibold outline-none hover:text-[hsl(var(--h-primary))] hover:underline focus-visible:text-[hsl(var(--h-primary))] focus-visible:underline')}>
              {item.label}
            </a>
          )}
        {external.from !== undefined && (
          <span data-ext-from="" className="min-w-0 max-w-[40%] flex-none truncate text-[hsl(var(--h-muted-foreground))]"
            title={external.from}>
            {external.from}
          </span>
        )}
      </span>
      {item.hint !== '' && (
        <Badge variant={PILL[item.tone]} data-tone={item.tone} className="flex-none px-[7px] py-0 text-[11px] font-bold">
          {item.hint}
        </Badge>
      )}
    </div>
  );
}
